// Auth Zustand store. Mirrors backend /api/auth/me response.
// Token storage is HttpOnly cookies (server-managed); the store only holds user/tenant metadata.

import { create } from 'zustand';

export type UserRole =
  | 'OWNER'
  | 'ADMIN'
  | 'ACCOUNTANT'
  | 'CASHIER'
  | 'SALES'
  | 'WAREHOUSE'
  | 'AUDITOR'
  | 'CA_PORTAL'
  | 'API';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  phone?: string | null;
  role: UserRole;
  status: 'ACTIVE' | 'INVITED' | 'SUSPENDED' | 'DEACTIVATED';
  lastLoginAt?: string | null;
  twoFactorEnabled: boolean;
}

export interface AuthTenant {
  id: string;
  slug: string;
  name: string;
  status: 'TRIAL' | 'ACTIVE' | 'SUSPENDED' | 'CHURNED';
  plan: 'INTERNAL' | 'SOLO' | 'GROWTH' | 'BUSINESS' | 'ENTERPRISE';
  trialEndsAt?: string | null;
}

export interface AuthCompany {
  id: string;
  name: string;
  baseCurrency: string;
}

export interface AuthState {
  status: 'unknown' | 'authenticating' | 'authenticated' | 'unauthenticated';
  user: AuthUser | null;
  tenant: AuthTenant | null;
  companies: AuthCompany[];
  activeCompanyId: string | null;
  activeBranchId: string | null;
  // State code the tax engine resolves for the supplier (branch ?? company
  // address). Null → vouchers default to IGST; forms show a nudge.
  supplierStateCode: string | null;
  error: string | null;
  setAuthenticated(payload: {
    user: AuthUser;
    tenant: AuthTenant;
    companies: AuthCompany[];
    activeCompanyId: string | null;
    activeBranchId: string | null;
    supplierStateCode?: string | null;
  }): void;
  setUnauthenticated(error?: string | null): void;
  setStatus(status: AuthState['status']): void;
  setActiveCompany(companyId: string | null, branchId?: string | null): void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'unknown',
  user: null,
  tenant: null,
  companies: [],
  activeCompanyId: null,
  activeBranchId: null,
  supplierStateCode: null,
  error: null,
  setAuthenticated: (payload) => {
    // Hint for the API client: a session existed here, so a 401 is worth an
    // auto-refresh. Without it a logged-out boot fires /me + /refresh — two
    // red console errors before the login page even paints.
    try { localStorage.setItem('orenza:session', '1'); } catch { /* private mode */ }
    set({
      status: 'authenticated',
      user: payload.user,
      tenant: payload.tenant,
      companies: payload.companies,
      activeCompanyId: payload.activeCompanyId,
      activeBranchId: payload.activeBranchId,
      supplierStateCode: payload.supplierStateCode ?? null,
      error: null,
    });
  },
  setUnauthenticated: (error = null) => {
    try { localStorage.removeItem('orenza:session'); } catch { /* private mode */ }
    set({
      status: 'unauthenticated',
      user: null,
      tenant: null,
      companies: [],
      activeCompanyId: null,
      activeBranchId: null,
      supplierStateCode: null,
      error,
    });
  },
  setStatus: (status) => set({ status }),
  setActiveCompany: (companyId, branchId = null) =>
    set({ activeCompanyId: companyId, activeBranchId: branchId }),
}));

// Listener for the auth-expired event from the API client.
if (typeof window !== 'undefined') {
  window.addEventListener('orenza:auth-expired', () => {
    useAuthStore.getState().setUnauthenticated('Session expired. Please sign in again.');
  });
}
